import { InjectionToken } from '@angular/core';
import type {
  CreateGalleryResult,
  GalleryCategoryFilterOption,
  GalleryListItem,
  GalleryPage,
  GallerySort,
  GalleryStatus,
  GalleryTrashItem,
} from '../entities/galleries.models';
import { matchesSearchQuery } from '../../util/search-query';

export interface GalleryPageParams {
  cursor?: string | null;
  perPage?: number;
  // One id or several — a gallery matching ANY selected category stays in the list.
  categoryId?: string | string[] | null;
  // The photographer's search words; the server compiles them (title + gallery date).
  q?: string;
  // One of the shared vocabulary's five orders. Absent — the route's own default order, which is
  // what the trash wants: «сначала те, что удалятся раньше» is the server's, not a menu item.
  sort?: GallerySort | null;
}

// Why a per-gallery write failed, in the words the batch result dialog shows. `null` = it worked.
// A single-gallery caller only asks «did it work» (`=== null`); a batch needs the reason per row.
// `too-many` is the server's per-gallery 3-category ceiling: the dialog cannot see it coming (it
// knows what the whole selection shares, not what each gallery already carries), so the refusal has
// to arrive nameable — «ошибка» would hide the one cause the photographer can act on.
export type GalleryOpFailure = 'no-access' | 'missing' | 'too-many' | 'error';

export interface GalleriesDataPort {
  list(params?: GalleryPageParams): Promise<GalleryPage<GalleryListItem> | null>;
  create(title: string, presetId?: string | null): Promise<CreateGalleryResult>;
  // Spec 01: clone a gallery's settings/design/structure into a fresh empty one; returns the new id (null on failure).
  duplicate(galleryId: string): Promise<string | null>;
  rename(galleryId: string, title: string): Promise<boolean>;
  setStatus(galleryId: string, status: GalleryStatus): Promise<GalleryOpFailure | null>;
  delete(galleryId: string): Promise<GalleryOpFailure | null>;
  // The category DIFF on ONE gallery — the server route already speaks add/remove, so a batch is
  // this call per gallery, never a new bulk endpoint. `createNames` mints missing names server-side.
  applyCategories(
    galleryId: string,
    add: readonly string[],
    remove: readonly string[],
    createNames?: readonly string[],
  ): Promise<GalleryOpFailure | null>;
  trash(params?: GalleryPageParams): Promise<GalleryPage<GalleryTrashItem> | null>;
  restore(galleryId: string): Promise<boolean>;
  purge(galleryId: string): Promise<boolean>;
  emptyTrash(): Promise<boolean>;
  categoryVocabulary(): Promise<GalleryCategoryFilterOption[] | null>;
}

export const GALLERIES_DATA = new InjectionToken<GalleriesDataPort>('GALLERIES_DATA');

const MOCK_CATEGORIES: GalleryCategoryFilterOption[] = [
  { id: 'cat-00000000-0000-4000-8000-000000000001', name: 'Выпускные', galleryCount: 1 },
  { id: 'cat-00000000-0000-4000-8000-000000000002', name: 'Семейные', galleryCount: 1 },
];

const MOCK_ITEMS: GalleryListItem[] = [
  {
    id: 'aaaaaaa1-0000-4000-8000-000000000001',
    title: 'Выпускной 2026',
    status: 'published',
    shootDate: '2026-06-20',
    createdAt: '2026-06-21T10:00:00.000Z',
    coverThumbnailUrl: null,
    photoCount: 214,
    categoryIds: ['cat-00000000-0000-4000-8000-000000000001'],
    views: 128,
    uniqueSessions: 0,
    sizeBytes: 3_221_225_472,
    urlSlug: 'vypusknoy-2026',
  },
  {
    id: 'aaaaaaa1-0000-4000-8000-000000000002',
    title: 'Smoke Gallery',
    status: 'published',
    shootDate: null,
    createdAt: '2026-06-10T08:30:00.000Z',
    coverThumbnailUrl: null,
    photoCount: 4,
    categoryIds: [],
    views: 12,
    uniqueSessions: 0,
    sizeBytes: 41_943_040,
    urlSlug: 'smoke-gallery',
  },
  {
    id: 'aaaaaaa1-0000-4000-8000-000000000003',
    title: 'Черновик семейной съёмки',
    status: 'hidden',
    shootDate: '2026-05-02',
    createdAt: '2026-05-03T15:12:00.000Z',
    coverThumbnailUrl: null,
    photoCount: 0,
    categoryIds: ['cat-00000000-0000-4000-8000-000000000002'],
    views: 0,
    uniqueSessions: 0,
    sizeBytes: 0,
    urlSlug: 'chernovik-semeynoy-syomki',
  },
];

export class MockGalleriesData implements GalleriesDataPort {
  private readonly items = structuredClone(MOCK_ITEMS);
  private readonly trashed: GalleryTrashItem[] = [];
  // The vocabulary is state, not a constant: a name typed in the batch dialog is minted here
  // exactly as the server mints it, so the next read returns it.
  private readonly categories = structuredClone(MOCK_CATEGORIES);

  async list(params?: GalleryPageParams): Promise<GalleryPage<GalleryListItem> | null> {
    const ids = params?.categoryId ? [params.categoryId].flat() : [];
    const all = structuredClone(this.items);
    const filtered = all.filter(
      item => (ids.length === 0 || ids.some(id => item.categoryIds.includes(id))) && matchesSearchQuery(item, params?.q ?? ''),
    );
    return this.paginate(filtered, params);
  }

  async create(title: string, _presetId?: string | null): Promise<CreateGalleryResult> {
    const trimmed = title.trim();
    if (!trimmed) return { ok: false, reason: 'invalid' };
    const id = `mock-${this.items.length + 1}`;
    this.items.unshift({
      id,
      title: trimmed,
      status: 'hidden',
      shootDate: null,
      createdAt: new Date().toISOString(),
      coverThumbnailUrl: null,
      photoCount: 0,
      categoryIds: [],
      views: 0,
      uniqueSessions: 0,
      sizeBytes: 0,
      urlSlug: `mock-slug-${id}`,
    });
    return {
      ok: true,
      gallery: { galleryId: id, title: trimmed, urlSlug: `mock-slug-${id}`, publicUrl: `/g/mock-token-${id}` },
    };
  }

  async duplicate(galleryId: string): Promise<string | null> {
    const source = this.items.find(entry => entry.id === galleryId);
    if (!source) return null;
    const id = `mock-dup-${this.items.length + 1}`;
    this.items.unshift({
      ...source,
      id,
      title: `${source.title} (копия)`,
      status: 'hidden',
      createdAt: new Date().toISOString(),
      coverThumbnailUrl: null,
      photoCount: 0,
    });
    return id;
  }

  async rename(galleryId: string, title: string): Promise<boolean> {
    const trimmed = title.trim();
    const item = this.items.find(entry => entry.id === galleryId);
    if (!item || !trimmed) return false;
    item.title = trimmed;
    return true;
  }

  async setStatus(galleryId: string, status: GalleryStatus): Promise<GalleryOpFailure | null> {
    const item = this.items.find(entry => entry.id === galleryId);
    if (!item) return 'missing';
    item.status = status;
    return null;
  }

  async delete(galleryId: string): Promise<GalleryOpFailure | null> {
    const index = this.items.findIndex(entry => entry.id === galleryId);
    if (index === -1) return 'missing';
    const [removed] = this.items.splice(index, 1);
    this.trashed.unshift({
      id: removed.id,
      title: removed.title,
      deletedAt: new Date().toISOString(),
      // Mock stand-in for the server clock: created day + 120 days, 23:59:59 UTC.
      expiresAt: new Date(new Date(removed.createdAt.slice(0, 10)).getTime() + 121 * 86_400_000 - 1000).toISOString(),
      coverThumbnailUrl: removed.coverThumbnailUrl,
      photoCount: removed.photoCount,
      shootDate: removed.shootDate,
      createdAt: removed.createdAt,
      categoryIds: removed.categoryIds,
      views: removed.views,
      uniqueSessions: removed.uniqueSessions,
      sizeBytes: removed.sizeBytes,
      urlSlug: removed.urlSlug,
    });
    return null;
  }

  async applyCategories(
    galleryId: string,
    add: readonly string[],
    remove: readonly string[],
    createNames: readonly string[] = [],
  ): Promise<GalleryOpFailure | null> {
    const item = this.items.find(entry => entry.id === galleryId);
    if (!item) return 'missing';
    const minted = createNames.map(name => this.mintCategory(name));
    item.categoryIds = Array.from(new Set([...item.categoryIds.filter(id => !remove.includes(id)), ...add, ...minted]));
    return null;
  }

  /** A typed name gets its id once; the second gallery of the same batch reuses it. */
  private mintCategory(name: string): string {
    const existing = this.categories.find(option => option.name === name);
    if (existing) return existing.id;
    const id = `cat-00000000-0000-4000-8000-${String(this.categories.length + 1).padStart(12, '0')}`;
    this.categories.push({ id, name, galleryCount: 0 });
    return id;
  }

  async trash(params?: GalleryPageParams): Promise<GalleryPage<GalleryTrashItem> | null> {
    return this.paginate(structuredClone(this.trashed), params);
  }

  // Index-cursor paging over a static array (the mock's stand-in for the keyset
  // page): the cursor is the next start index.
  private paginate<T>(all: T[], params?: GalleryPageParams): GalleryPage<T> {
    const perPage = params?.perPage && params.perPage > 0 ? params.perPage : 24;
    const start = params?.cursor ? Number(params.cursor) : 0;
    const items = all.slice(start, start + perPage);
    const nextStart = start + items.length;
    const hasMore = nextStart < all.length;
    return { items, total: all.length, hasMore, nextCursor: hasMore ? String(nextStart) : null };
  }

  async restore(galleryId: string): Promise<boolean> {
    const index = this.trashed.findIndex(entry => entry.id === galleryId);
    if (index === -1) return false;
    const [restored] = this.trashed.splice(index, 1);
    this.items.unshift({
      id: restored.id,
      title: restored.title,
      status: 'hidden',
      shootDate: null,
      createdAt: new Date().toISOString(),
      coverThumbnailUrl: restored.coverThumbnailUrl,
      photoCount: restored.photoCount,
      categoryIds: [],
      views: restored.views,
      uniqueSessions: restored.uniqueSessions,
      sizeBytes: restored.sizeBytes,
      urlSlug: restored.urlSlug,
    });
    return true;
  }

  async purge(galleryId: string): Promise<boolean> {
    const index = this.trashed.findIndex(entry => entry.id === galleryId);
    if (index === -1) return false;
    this.trashed.splice(index, 1);
    return true;
  }

  async emptyTrash(): Promise<boolean> {
    this.trashed.length = 0;
    return true;
  }

  async categoryVocabulary(): Promise<GalleryCategoryFilterOption[] | null> {
    return structuredClone(this.categories);
  }
}
